import React, { useState, useCallback, useMemo } from 'react';
import { View, Text, Image, Pressable, StyleSheet, ScrollView, ActivityIndicator } from 'react-native-web';
import ReactPlayer from 'react-player/lazy';
import Button from './Button';
import CreatorModal from './CreatorModal';
import { getCreatorsAesthetics } from '../services/CreatorService';
import { getCreatorRatings } from '../services/BusinessService';

// Custom hook for creator interactions
const useCreatorInteractions = () => {
  const [loadingCreatorId, setLoadingCreatorId] = useState(null);

  const handleAddToCapaign = useCallback((creator, onAddCreator) => {
    if (loadingCreatorId) return;
    
    setLoadingCreatorId(creator.id);
    
    // Simulate a small delay for better UX feedback
    setTimeout(() => {
      onAddCreator(creator);
      setLoadingCreatorId(null);
    }, 300);
  }, [loadingCreatorId]);

  return {
    handleAddToCapaign,
    loadingCreatorId,
  };
};

// Individual creator card component
const CreatorCard = React.memo(({ creator, onAddToCapaign, onCreatorPress, onAestheticPress, isLoading }) => {
  const profileImageSource = useMemo(() => ({
    uri: `https://d3p3fw3rutb1if.cloudfront.net/users-preview/${creator.id}`
  }), [creator.id]);

  const formatFollowerCount = useCallback((count) => {
    if (count >= 1000000) {
      return `${(count / 1000000).toFixed(1)}M`;
    } else if (count >= 1000) {
      return `${(count / 1000).toFixed(1)}K`;
    }
    return count?.toString() || '0';
  }, []);

  const formattedFollowerCount = useMemo(() =>
    formatFollowerCount(creator.follower_count),
    [creator.follower_count, formatFollowerCount]
  );

  return (
    <View style={styles.creatorCard}>
      <Pressable
        style={({ hovered }) => [
          styles.creatorHeader,
          hovered && styles.creatorHeaderHovered
        ]}
        onPress={() => onCreatorPress(creator)}
        accessible={true}
        accessibilityLabel={`View ${creator.username} profile`}
        accessibilityRole="button"
      >
        <Image
          source={profileImageSource}
          style={styles.creatorImage}
          accessible={true}
          accessibilityLabel={`${creator.username} profile picture`}
        />
        <View style={styles.creatorInfo}>
          <Text style={styles.username} numberOfLines={1}>
            @{creator.username}
          </Text>
          <Text style={styles.followerCount} numberOfLines={1}>
            {formattedFollowerCount} followers
          </Text>
        </View>
      </Pressable>

      <View style={styles.addButtonContainer}>
        <Button 
          text={isLoading ? "Adding..." : "+ Campaign"} 
          style={[
            styles.addButton,
            isLoading && styles.addButtonLoading
          ]}
          onPress={() => onAddToCapaign(creator)}
          disabled={isLoading}
        />
      </View>

      {creator.aesthetics && creator.aesthetics.length > 0 && (
        <View style={styles.section}>
          <Text style={styles.sectionTitle}>Aesthetics</Text>
          <View style={styles.aestheticsContainer}>
            {creator.aesthetics.map((aesthetic, index) => (
              <Pressable
                key={index}
                style={({ hovered }) => [
                  styles.aestheticTag,
                  hovered && styles.aestheticTagHovered
                ]}
                onPress={() => onAestheticPress(creator, aesthetic)}
                accessible={true}
                accessibilityLabel={`View ${aesthetic} content for ${creator.username}`}
                accessibilityRole="button"
              >
                <Text style={styles.aestheticText} numberOfLines={1}>
                  {aesthetic}
                </Text>
              </Pressable>
            ))}
          </View>
        </View>
      )}

      {creator.content && creator.content.length > 0 && (
        <View style={styles.section}>
          <Text style={styles.sectionTitle}>Content</Text>
          <ScrollView 
            horizontal 
            showsHorizontalScrollIndicator={false}
            style={styles.contentScrollView}
            contentContainerStyle={styles.contentContainer}
          >
            {creator.content.map((post, index) => (
              <CreatorContentItem key={post.img_preview_id || index} post={post} />
            ))}
          </ScrollView>
        </View>
      )}
    </View>
  );
});

// Individual content item component
const CreatorContentItem = React.memo(({ post }) => {
  const [isLoading, setIsLoading] = useState(true);

  const handleVideoReady = useCallback(() => {
    setIsLoading(false);
  }, []);

  const handleImageLoad = useCallback(() => {
    setIsLoading(false);
  }, []);

  if (post.content_type === "VIDEO") {
    return (
      <View style={styles.contentItem}>
        <View style={styles.videoWrapper}>
          {isLoading && (
            <View style={styles.videoLoading}>
              <ActivityIndicator size="small" color="#396de0" />
            </View>
          )}
          <ReactPlayer
            style={styles.videoPlayer}
            width="100%"
            height="100%"
            url={`https://d3p3fw3rutb1if.cloudfront.net/videos/${post.img_preview_id}`}
            muted={true}
            playing={true}
            loop={true}
            onReady={handleVideoReady}
            onError={() => setIsLoading(false)}
          />
        </View>
      </View>
    );
  }

  return (
    <View style={styles.contentItem}>
      {isLoading && (
        <View style={styles.imageLoading}>
          <ActivityIndicator size="small" color="#396de0" />
        </View>
      )}
      <Image
        source={{ uri: `https://d3p3fw3rutb1if.cloudfront.net/photos/${post.img_preview_id}` }}
        style={styles.contentImage}
        onLoad={handleImageLoad}
        onError={() => setIsLoading(false)}
        accessible={true}
        accessibilityLabel="Creator content image"
      />
    </View>
  );
});

// Empty state component
const EmptyCreators = React.memo(() => (
  <View style={styles.emptyContainer}>
    <Text style={styles.emptyIcon}>👥</Text>
    <Text style={styles.emptyTitle}>No featured creators yet</Text>
    <Text style={styles.emptyDescription}>
      Check back soon for amazing creators we're featuring!
    </Text>
  </View>
));

// Loading skeleton component
const CreatorsSkeleton = React.memo(() => (
  <ScrollView 
    horizontal 
    showsHorizontalScrollIndicator={false}
    style={styles.scrollView}
    contentContainerStyle={styles.scrollContainer}
  >
    {[1, 2, 3].map((index) => (
      <View key={index} style={styles.skeletonCard}>
        <View style={styles.skeletonHeader}>
          <View style={styles.skeletonImage} />
          <View style={styles.skeletonInfo}>
            <View style={styles.skeletonTextLong} />
            <View style={styles.skeletonTextShort} />
          </View>
        </View>
        <View style={styles.skeletonButton} />
        <View style={styles.skeletonSection}>
          <View style={styles.skeletonSectionTitle} />
          <View style={styles.skeletonTags}>
            <View style={styles.skeletonTag} />
            <View style={styles.skeletonTag} />
          </View>
        </View>
      </View>
    ))}
  </ScrollView>
));

const CreatorSpotlight = React.memo(({ creators, onAddCreator, isLoading }) => {
  const { handleAddToCapaign, loadingCreatorId } = useCreatorInteractions();
  const [creatorModal, setCreatorModal] = useState(false);
  const [selectedCreator, setSelectedCreator] = useState({});
  const [creatorsAesthetics, setCreatorsAesthetics] = useState([]);
  const [selectedAesthetic, setSelectedAesthetic] = useState(null);

  const sortedCreators = useMemo(() => {
    if (!creators || !Array.isArray(creators)) return [];

    // Sort by follower count descending, then by username
    return [...creators].sort((a, b) => {
      const followerDiff = (b.follower_count || 0) - (a.follower_count || 0);
      if (followerDiff !== 0) return followerDiff;
      return (a.username || '').localeCompare(b.username || '');
    });
  }, [creators]);

  const handleCreatorAction = useCallback((creator) => {
    handleAddToCapaign(creator, onAddCreator);
  }, [handleAddToCapaign, onAddCreator]);

  const handleAestheticPress = useCallback((creator, aesthetic) => {
    openCreatorModal(creator, aesthetic);
  }, []);

  const openCreatorModal = useCallback(async (creator, aesthetic = null) => {
    // Store the selected aesthetic
    setSelectedAesthetic(aesthetic);

    // Fetch both aesthetics and ratings in parallel
    try {
      const [aestheticsResp, ratingsResp] = await Promise.all([
        getCreatorsAesthetics(creator.id),
        getCreatorRatings(creator.id)
      ]);

      // Update aesthetics
      if (aestheticsResp?.status === 200 && aestheticsResp?.data) {
        setCreatorsAesthetics(aestheticsResp.data);
      } else {
        setCreatorsAesthetics([]);
      }

      // Merge ratings data with creator object
      let creatorWithRatings = { ...creator };
      if (ratingsResp?.status === 200 && ratingsResp?.data) {
        creatorWithRatings = {
          ...creator,
          avg_quality_rating: ratingsResp.data.avg_quality_rating,
          avg_responsiveness_rating: ratingsResp.data.avg_responsiveness_rating
        };
      }

      setSelectedCreator(creatorWithRatings);
      setCreatorModal(true);
    } catch (err) {
      console.error('Error fetching creator data:', err);
      // Still open modal with basic creator data
      setSelectedCreator(creator);
      setCreatorsAesthetics([]);
      setCreatorModal(true);
    }
  }, []);

  const closeModal = useCallback(() => {
    setCreatorModal(false);
    setSelectedAesthetic(null);
    setSelectedCreator({});
    setCreatorsAesthetics([]);
  }, []);

  if (isLoading) {
    return (
      <View style={styles.container}>
        <Text style={styles.title}>Creators We're Loving</Text>
        <CreatorsSkeleton />
      </View>
    );
  }

  if (!sortedCreators || sortedCreators.length === 0) {
    return (
      <View style={styles.container}>
        <Text style={styles.title}>Creators We're Loving</Text>
        <EmptyCreators />
      </View>
    );
  }

  return (
    <View style={styles.container}>
      <Text style={styles.title}>Creators We're Loving</Text>
      <ScrollView 
        horizontal 
        showsHorizontalScrollIndicator={false}
        style={styles.scrollView}
        contentContainerStyle={styles.scrollContainer}
        decelerationRate="fast"
        snapToInterval={280} // Card width + margin
        snapToAlignment="start"
      >
        {sortedCreators.map((creator) => (
          <CreatorCard
            key={creator.id}
            creator={creator}
            onAddToCapaign={handleCreatorAction}
            onCreatorPress={openCreatorModal}
            onAestheticPress={handleAestheticPress}
            isLoading={loadingCreatorId === creator.id}
          />
        ))}
      </ScrollView>
      <CreatorModal
        isOpen={creatorModal}
        onClose={closeModal}
        selectedCreator={selectedCreator}
        creatorsAesthetics={creatorsAesthetics}
        initialAesthetic={selectedAesthetic}
      />
    </View>
  );
});

const styles = StyleSheet.create({
  container: {
    marginTop: 30,
    marginBottom: 30,
    marginRight: 20,
  },
  title: {
    fontSize: 24,
    fontWeight: 'bold',
    color: '#396de0',
    fontFamily: "'forma-djr-display', sans-serif",
    marginBottom: 20,
  },
  scrollView: {
    overflow: 'visible',
  },
  scrollContainer: {
    paddingRight: 30,
  },
  creatorCard: {
    backgroundColor: 'white',
    borderRadius: 12,
    padding: 16,
    marginRight: 20,
    width: 260,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 8,
    elevation: 3,
    borderWidth: 1,
    borderColor: '#f3f4f6',
  },
  creatorHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    borderRadius: 8,
    padding: 8,
    margin: -8,
    marginBottom: 7,
    transition: 'all 0.2s ease',
    outlineStyle: 'none',
    borderWidth: 0,
    cursor: 'pointer',
  },
  creatorHeaderHovered: {
    backgroundColor: '#f9fafb',
    transform: [{ scale: 1.02 }],
  },
  creatorImage: {
    width: 50,
    height: 50,
    borderRadius: 25,
    marginRight: 12,
    backgroundColor: '#f3f4f6',
  },
  creatorInfo: {
    flex: 1,
  },
  username: {
    fontSize: 16,
    fontWeight: '600',
    color: '#396de0',
    fontFamily: "'lato', sans-serif",
    marginBottom: 4,
  },
  followerCount: {
    fontSize: 14,
    color: '#6b7280',
    fontFamily: "'lato', sans-serif",
  },
  addButtonContainer: {
    alignItems: 'center',
    marginBottom: 20,
  },
  addButton: {
    paddingHorizontal: 20,
    height: 36,
    minWidth: 120,
  },
  addButtonLoading: {
    opacity: 0.7,
  },
  section: {
    marginBottom: 16,
  },
  sectionTitle: {
    fontSize: 12,
    fontWeight: '600',
    color: '#9ca3af',
    fontFamily: "'lato', sans-serif",
    marginBottom: 8,
    textTransform: 'uppercase',
    letterSpacing: 0.5,
  },
  aestheticsContainer: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 6,
  },
  aestheticTag: {
    backgroundColor: '#f3f4f6',
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 12,
    marginBottom: 4,
    transition: 'all 0.2s ease',
    cursor: 'pointer',
    outlineStyle: 'none',
    borderWidth: 0,
  },
  aestheticTagHovered: {
    backgroundColor: '#e5e7eb',
    transform: [{ scale: 1.05 }],
  },
  aestheticText: {
    fontSize: 12,
    color: '#6b7280',
    fontFamily: "'lato', sans-serif",
    fontWeight: '500',
  },
  contentScrollView: {
    marginHorizontal: -2,
  },
  contentContainer: {
    paddingHorizontal: 2,
    gap: 8,
  },
  contentItem: {
    width: 75,
    height: 100,
    borderRadius: 8,
    overflow: 'hidden',
    backgroundColor: '#f3f4f6',
    position: 'relative',
  },
  contentImage: {
    width: '100%',
    height: '100%',
    resizeMode: 'cover',
  },
  videoWrapper: {
    width: '100%',
    height: '100%',
    position: 'relative',
  },
  videoPlayer: {
    width: '100%',
    height: '100%',
  },
  videoLoading: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: '#f3f4f6',
    zIndex: 2,
  },
  imageLoading: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: '#f3f4f6',
    zIndex: 2,
  },

  emptyContainer: {
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 60,
    paddingHorizontal: 40,
  },
  emptyIcon: {
    fontSize: 48,
    marginBottom: 16,
    opacity: 0.5,
  },
  emptyTitle: {
    fontSize: 18,
    fontWeight: '600',
    color: '#374151',
    fontFamily: "'forma-djr-display', sans-serif",
    marginBottom: 8,
    textAlign: 'center',
  },
  emptyDescription: {
    fontSize: 14,
    color: '#6b7280',
    fontFamily: "'lato', sans-serif",
    textAlign: 'center',
    lineHeight: 20,
    maxWidth: 280,
  },
  skeletonCard: {
    backgroundColor: '#f9fafb',
    borderRadius: 12,
    padding: 16,
    marginRight: 20,
    width: 260,
  },
  skeletonHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 15,
  },
  skeletonImage: {
    width: 50,
    height: 50,
    borderRadius: 25,
    backgroundColor: '#e5e7eb',
    marginRight: 12,
  },
  skeletonInfo: {
    flex: 1,
  },
  skeletonTextLong: {
    height: 16,
    backgroundColor: '#e5e7eb',
    borderRadius: 4,
    marginBottom: 6,
    width: '80%',
  },
  skeletonTextShort: {
    height: 14,
    backgroundColor: '#e5e7eb',
    borderRadius: 4,
    width: '60%',
  },
  skeletonButton: {
    height: 36,
    backgroundColor: '#e5e7eb',
    borderRadius: 18,
    marginBottom: 20,
  },
  skeletonSection: {
    marginBottom: 16,
  },
  skeletonSectionTitle: {
    height: 12,
    backgroundColor: '#e5e7eb',
    borderRadius: 4,
    marginBottom: 8,
    width: '40%',
  },
  skeletonTags: {
    flexDirection: 'row',
    gap: 6,
  },
  skeletonTag: {
    height: 24,
    backgroundColor: '#e5e7eb',
    borderRadius: 12,
    width: 60,
  },
});

CreatorCard.displayName = 'CreatorCard';
CreatorContentItem.displayName = 'CreatorContentItem';
EmptyCreators.displayName = 'EmptyCreators';
CreatorsSkeleton.displayName = 'CreatorsSkeleton';
CreatorSpotlight.displayName = 'CreatorSpotlight';

export default CreatorSpotlight; 